import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL, HAS_API_URL } from '../config/api';

const CONTAINER_STYLE = {
  maxWidth: 1200,
  margin: '32px auto',
  padding: '0 16px',
  fontFamily:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  color: '#111',
};

const IMAGE_WRAPPER_STYLE = {
  position: 'relative',
  display: 'inline-block',
  lineHeight: 0,
  maxWidth: '100%',
};

const IMAGE_STYLE = {
  display: 'block',
  maxWidth: '100%',
  height: 'auto',
  borderRadius: 8,
  boxShadow: '0 4px 18px rgba(0,0,0,0.15)',
};

const BOX_STYLE_BASE = {
  position: 'absolute',
  border: '2px solid rgba(255, 255, 255, 0.95)',
  background: 'rgba(34, 197, 94, 0.18)',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.45) inset',
  borderRadius: 4,
  pointerEvents: 'none',
  boxSizing: 'border-box',
};

const LABEL_STYLE = {
  position: 'absolute',
  left: 0,
  top: -22,
  background: 'rgba(17, 24, 39, 0.92)',
  color: '#fff',
  fontSize: 12,
  lineHeight: '18px',
  padding: '0 6px',
  borderRadius: 4,
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const HOTSPOT_DOT_STYLE = {
  position: 'absolute',
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: 'rgba(239, 68, 68, 0.95)',
  border: '2px solid rgba(255, 255, 255, 0.95)',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
  boxSizing: 'border-box',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
};

function DevBoxTestPage() {
  const [searchParams] = useSearchParams();
  const imageUrl = (searchParams.get('img') || '').trim();
  const { user, loading: authLoading } = useAuth();

  const imgRef = useRef(null);
  const [imgRect, setImgRect] = useState({ width: 0, height: 0 });
  const [detection, setDetection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const measure = useCallback(() => {
    const node = imgRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    setImgRect((prev) =>
      Math.abs(prev.width - width) < 0.5 && Math.abs(prev.height - height) < 0.5
        ? prev
        : { width, height },
    );
    console.log('[dev/box-test] rendered image size:', width, height);
  }, []);

  useEffect(() => {
    console.log('[dev/box-test] imageUrl:', imageUrl);
  }, [imageUrl]);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!imageUrl) {
      setError('Missing ?img= query parameter.');
      return undefined;
    }
    if (!HAS_API_URL) {
      setError('REACT_APP_API_URL is not configured.');
      return undefined;
    }
    if (!user) {
      setError('You must be signed in to run the dev detector.');
      return undefined;
    }

    let cancelled = false;

    async function run() {
      setLoading(true);
      setError('');
      setDetection(null);
      try {
        const idToken = await user.getIdToken(true);
        const res = await fetch(`${API_BASE_URL}/api/dev/detect-furniture-boxes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ imageUrl }),
        });
        if (cancelled) return;
        const text = await res.text();
        let body = null;
        try {
          body = text ? JSON.parse(text) : null;
        } catch {
          /* ignore */
        }
        if (!res.ok) {
          const msg =
            (body && typeof body.error === 'string' && body.error) ||
            `Request failed (${res.status}).`;
          setError(msg);
          console.error('[dev/box-test] request failed:', res.status, body);
          return;
        }
        const validCount = Array.isArray(body?.items) ? body.items.length : 0;
        console.log('[dev/box-test] vision model:', body?.model ?? '(unknown)');
        console.log('[dev/box-test] cache used (cached):', body?.cached ?? false, '(dev route: no server cache)');
        console.log(
          '[dev/box-test] legacy bbox cache rejected:',
          body?.legacyBboxCacheRejected ?? false,
        );
        console.log('[dev/box-test] detection JSON:', body);
        console.log('[dev/box-test] valid detected items:', validCount);
        if (Array.isArray(body?.items)) {
          body.items.forEach((it, i) => {
            const b = it?.bbox_1000;
            console.log('[dev/box-test] item bbox format', {
              index: i,
              id: it?.id,
              bboxKeys: b && typeof b === 'object' ? Object.keys(b).sort().join(',') : '',
              bbox_1000: b,
            });
          });
        }
        setDetection(body);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        console.error('[dev/box-test] fetch threw:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [imageUrl, user, authLoading]);

  useEffect(() => {
    function onResize() {
      measure();
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measure]);

  const items = Array.isArray(detection?.items) ? detection.items : [];
  const renderedWidth = imgRect.width;
  const renderedHeight = imgRect.height;

  useEffect(() => {
    if (!detection) return;
    console.log('[dev/box-test] rendered image width/height (layout):', renderedWidth, renderedHeight);
  }, [detection, renderedWidth, renderedHeight]);

  return (
    <main className="dev-box-test-page" style={CONTAINER_STYLE}>
      <h1 style={{ fontSize: 22, margin: '0 0 8px' }}>Furniture box detector (dev)</h1>
      <p style={{ margin: '0 0 16px', color: '#444', fontSize: 14 }}>
        Hidden dev-only route. No credits deducted. See the browser console for the full JSON
        and rendered image size.
      </p>

      {!imageUrl ? (
        <p style={{ color: '#b91c1c' }}>
          Provide an image URL via the <code>?img=</code> query parameter.
        </p>
      ) : null}

      {error ? (
        <p style={{ color: '#b91c1c', whiteSpace: 'pre-wrap' }}>{error}</p>
      ) : null}

      {loading ? (
        <p style={{ color: '#374151' }}>Detecting furniture&hellip;</p>
      ) : null}

      {imageUrl ? (
        <div style={IMAGE_WRAPPER_STYLE}>
          <img
            ref={imgRef}
            src={imageUrl}
            alt="Generated design under analysis"
            style={IMAGE_STYLE}
            onLoad={measure}
            crossOrigin="anonymous"
          />
          {renderedWidth > 0 && renderedHeight > 0
            ? items.map((item, idx) => {
                const bbox = item?.bbox_1000;
                if (!bbox) return null;
                const x1 = Number(bbox.x1);
                const y1 = Number(bbox.y1);
                const x2 = Number(bbox.x2);
                const y2 = Number(bbox.y2);
                if (![x1, y1, x2, y2].every(Number.isFinite)) return null;
                const left = (x1 / 1000) * renderedWidth;
                const top = (y1 / 1000) * renderedHeight;
                const width = ((x2 - x1) / 1000) * renderedWidth;
                const height = ((y2 - y1) / 1000) * renderedHeight;
                if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
                if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
                if (width <= 0 || height <= 0) return null;
                const key =
                  typeof item.id === 'string' && item.id ? item.id : `item-${idx}`;
                const confidencePct = Number.isFinite(Number(item.confidence))
                  ? Math.round(Number(item.confidence) * 100)
                  : null;
                const labelText =
                  (typeof item.label === 'string' && item.label) ||
                  (typeof item.category === 'string' && item.category) ||
                  'item';
                const point = item?.point_1000;
                const dotLeft =
                  point && Number.isFinite(Number(point.x))
                    ? (Number(point.x) / 1000) * renderedWidth
                    : null;
                const dotTop =
                  point && Number.isFinite(Number(point.y))
                    ? (Number(point.y) / 1000) * renderedHeight
                    : null;
                const showDot =
                  dotLeft !== null &&
                  dotTop !== null &&
                  Number.isFinite(dotLeft) &&
                  Number.isFinite(dotTop);

                return (
                  <Fragment key={key}>
                    <div
                      style={{
                        ...BOX_STYLE_BASE,
                        left,
                        top,
                        width,
                        height,
                      }}
                    >
                      <span style={LABEL_STYLE}>
                        {labelText}
                        {confidencePct !== null ? ` · ${confidencePct}%` : ''}
                      </span>
                    </div>
                    {showDot ? (
                      <div
                        style={{
                          ...HOTSPOT_DOT_STYLE,
                          left: dotLeft,
                          top: dotTop,
                        }}
                      />
                    ) : null}
                  </Fragment>
                );
              })
            : null}
        </div>
      ) : null}

      {detection ? (
        <details style={{ marginTop: 24 }}>
          <summary style={{ cursor: 'pointer', color: '#374151', fontSize: 14 }}>
            Detected items ({items.length})
          </summary>
          <pre
            style={{
              marginTop: 8,
              padding: 12,
              background: '#f3f4f6',
              borderRadius: 6,
              fontSize: 12,
              overflowX: 'auto',
            }}
          >
            {JSON.stringify(detection, null, 2)}
          </pre>
        </details>
      ) : null}
    </main>
  );
}

export default DevBoxTestPage;
